/**
 * @module floorplan/unit-object
 * @category Helpers
 *
 * Binds the POS Area/Unit domain to generic engine objects — builds the
 * `table` object for a placed unit, resolves placement positions, and diffs a
 * unit list into status/label patches for the live floor.
 *
 */

import { StoreUnitStatus } from '@fuze-store/shared/enums';

import type { StoreUnit } from '@src/types/store/unit';

import type { FloorObject } from '@src/floorplan/models/object';
import { FLOOR_OBJECT_TYPE } from '@src/floorplan/models/object';
import { createFloorId } from '@src/floorplan/utils/id';

/** Default drawn size of a placed unit (world units). */

export const UNIT_OBJECT_SIZE = { width: 88, height: 64 };

/**
 * Status → fill/stroke colors. Tailwind-palette literals (NOT theme tokens):
 * these are SEMANTIC state colors that must read identically in light and
 * dark mode, mirroring the unit status badges elsewhere in the app.
 */
export const UNIT_STATUS_COLORS: Record<
  StoreUnitStatus,
  { fill: string; stroke: string }
> = {
  [StoreUnitStatus.AVAILABLE]: { fill: '#dcfce7', stroke: '#16a34a' },
  [StoreUnitStatus.OCCUPIED]: { fill: '#fee2e2', stroke: '#dc2626' },
  [StoreUnitStatus.RESERVED]: { fill: '#fef3c7', stroke: '#d97706' },
  [StoreUnitStatus.UNAVAILABLE]: { fill: '#e4e4e7', stroke: '#71717a' },
};

/** Status written on a table whose unit is no longer in the unit list. */
const MISSING_UNIT_STATUS = 'MISSING';

const statusColors = (
  status: StoreUnit['status'],
): { fill: string; stroke: string } => {
  const colors = UNIT_STATUS_COLORS[status];
  if (colors === undefined)
    return UNIT_STATUS_COLORS[StoreUnitStatus.AVAILABLE];
  return colors;
};

/**
 * The `#123` order-number sub-label for an OCCUPIED table, else `undefined`.
 * Mirrors the grid item's `#{orderNumber}` line — static (no ticking timer),
 * so it re-records only when the order changes, not per frame.
 */
const unitOrderLabel = (unit: StoreUnit): string | undefined => {
  if (unit.status !== StoreUnitStatus.OCCUPIED) return undefined;
  const orderNumber = unit.serviceMode?.order?.orderNumber;
  if (orderNumber === undefined || orderNumber === null) return undefined;
  return `#${orderNumber}`;
};

/** Build the `table` object for a unit placed at a world position. */
export const createUnitObject = (
  unit: StoreUnit,
  position: { x: number; y: number },
  options: { layerId: string; zIndex: number },
): FloorObject => {
  const colors = statusColors(unit.status);
  return {
    id: createFloorId('obj'),
    type: FLOOR_OBJECT_TYPE.TABLE,
    x: position.x,
    y: position.y,
    width: UNIT_OBJECT_SIZE.width,
    height: UNIT_OBJECT_SIZE.height,
    rotation: 0,
    scale: 1,
    visible: true,
    locked: false,
    zIndex: options.zIndex,
    layerId: options.layerId,
    style: {
      fill: colors.fill,
      stroke: colors.stroke,
      strokeWidth: 2,
      cornerRadius: 10,
      badge: colors.stroke,
      seats: unit.capacity ?? 0,
      text: unit.name,
      subLabel: unitOrderLabel(unit),
    },
    metadata: {
      unitId: unit.id,
      unitName: unit.name,
      capacity: unit.capacity,
      status: unit.status,
    },
  };
};

/**
 * Cascade a placement off any object already sitting at the same spot —
 * repeat "tap to place" without moving the camera would otherwise stack
 * every unit at the exact viewport center (fully overlapping, looks like
 * one object). Desktop-OS window-cascade: step down-right until free.
 */
export const resolvePlacementPosition = (
  objects: readonly FloorObject[],
  base: { x: number; y: number },
  step = 24,
): { x: number; y: number } => {
  const taken = new Set<string>();
  for (const object of objects) {
    taken.add(`${Math.round(object.x)}:${Math.round(object.y)}`);
  }
  let x = base.x;
  let y = base.y;
  let guard = 0;
  while (taken.has(`${Math.round(x)}:${Math.round(y)}`) && guard < 100) {
    x += step;
    y += step;
    guard += 1;
  }
  return { x, y };
};

/** The unitId a drawn object is linked to (null when unlinked). */
const objectUnitId = (object: FloorObject): string | null => {
  const raw = object.metadata['unitId'];
  return typeof raw === 'string' ? raw : null;
};

/** Set of unit ids already placed on the plan. */
export const placedUnitIds = (objects: readonly FloorObject[]): Set<string> => {
  const ids = new Set<string>();
  for (const object of objects) {
    const unitId = objectUnitId(object);
    if (unitId !== null) ids.add(unitId);
  }
  return ids;
};

/**
 * Diff live unit rows against drawn objects → the patch list that recolors
 * stale tables. Each patch touches ONE object's status metadata + colors
 * (name/capacity refresh ride along); the engine re-records the static
 * picture once per applied batch.
 */
export const buildUnitStatusPatches = (
  units: readonly StoreUnit[],
  objects: readonly FloorObject[],
): { id: string; patch: Partial<Omit<FloorObject, 'id'>> }[] => {
  const unitsById = new Map<string, StoreUnit>();
  for (const unit of units) unitsById.set(unit.id, unit);

  const patches: { id: string; patch: Partial<Omit<FloorObject, 'id'>> }[] = [];
  for (const object of objects) {
    const unitId = objectUnitId(object);
    if (unitId === null) continue;
    const unit = unitsById.get(unitId);
    if (unit === undefined) {
      // The unit was deleted (or moved off this list): grey the table out
      // and drop its order label, but keep the link so it comes back if
      // the unit shows up again.
      if (object.metadata['status'] === MISSING_UNIT_STATUS) continue;
      const greyed = UNIT_STATUS_COLORS[StoreUnitStatus.UNAVAILABLE];
      patches.push({
        id: object.id,
        patch: {
          style: {
            ...object.style,
            fill: greyed.fill,
            stroke: greyed.stroke,
            badge: greyed.stroke,
            subLabel: undefined,
          },
          metadata: { ...object.metadata, status: MISSING_UNIT_STATUS },
        },
      });
      continue;
    }
    const status = object.metadata['status'];
    const name = object.metadata['unitName'];
    const orderLabel = unitOrderLabel(unit);
    const currentSubLabel = object.style?.subLabel;
    // `badge === undefined` retrofits documents saved before the
    // production-visual pass (stroke ring / badge / seats) in one sync.
    if (
      status === unit.status &&
      name === unit.name &&
      currentSubLabel === orderLabel &&
      object.style?.badge !== undefined
    )
      continue;
    const colors = statusColors(unit.status);
    // A label staff changed on the plan (double-tap rename edits only
    // `style.text`, so it no longer matches the last synced unit name) is
    // kept; an untouched or empty one follows the unit, as does one with no
    // synced name to compare against (renamed-table-reverts-to-unit-name).
    const label = object.style?.text;
    const relabelled =
      typeof name === 'string' &&
      typeof label === 'string' &&
      label !== '' &&
      label !== name;
    patches.push({
      id: object.id,
      patch: {
        style: {
          ...object.style,
          fill: colors.fill,
          stroke: colors.stroke,
          strokeWidth: 2,
          cornerRadius: 10,
          badge: colors.stroke,
          seats: unit.capacity ?? 0,
          text: relabelled ? label : unit.name,
          subLabel: orderLabel,
        },
        metadata: {
          ...object.metadata,
          unitId: unit.id,
          unitName: unit.name,
          capacity: unit.capacity,
          status: unit.status,
        },
      },
    });
  }
  return patches;
};
